import { AxiosInstance, AxiosResponse } from 'axios';
import { axiosInstance, axiosInstanceProd } from './axios.ts';
import { RequestParameters, PaginatedResponse } from './types';

export const ebooksDomain = process.env.REACT_APP_API_URL?.includes('staging')
  ? 'beta.ebooks.app.br'
  : 'ebooks.app.br';

function getBookstoreBaseUrl(product: any): string | null {
  if (product?.url && typeof product.url === 'string' && product.url.startsWith('http')) {
    return product.url;
  }
  const metaUrl = product?.metadata?.url?.value;
  if (typeof metaUrl === 'string' && metaUrl.startsWith('http')) return metaUrl;
  const slug = product?.metadata?.slug?.value;
  if (slug) return `https://${slug}.${ebooksDomain}/`;
  return null;
}

export function preconnectBookstoreOrigins(productBenefits: any[]): void {
  if (typeof document === 'undefined' || !Array.isArray(productBenefits)) return;

  const origins = new Set<string>();
  for (const product of productBenefits) {
    if (!product?.default_product?.is_bookstore) continue;
    const baseUrl = getBookstoreBaseUrl(product);
    if (!baseUrl) continue;
    try {
      origins.add(new URL(baseUrl).origin);
    } catch {
      // url inválida — ignora
    }
  }

  origins.forEach((origin) => {
    if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = '';
    document.head.appendChild(link);
  });
}

export function snakeCaseToCamelCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/__/g, '_')
    .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
}

export function snakeCaseDictToCamelCaseDict(object: Record<string, any> | any | any[]): Record<string, any> {
  const newObj: Record<string, any> = {};

  if (!(object instanceof Array) && !(typeof object === 'object')) return object;

  if (object instanceof Array) return object.map((e: Record<any, any>) => snakeCaseDictToCamelCaseDict(e));

  Object.entries(object).forEach(([key, value]: [key: string, value: any]) => {
    if (value instanceof Array) value = value.map((e: Record<any, any>) => snakeCaseDictToCamelCaseDict(e));
    else if (typeof value === 'object' && value) value = snakeCaseDictToCamelCaseDict(value);
    newObj[snakeCaseToCamelCase(key)] = value;
  });

  return newObj;
}

export function snakeCaseResultToCamelCaseResult(result: Record<string, any>[]): Record<string, any>[] {
  return result.map((r) => snakeCaseDictToCamelCaseDict(r));
}

export function cleanCPFCNPJ(code: string): string {
  return code.replace(/\./g, '').replaceAll('-', '').replaceAll('/', '').replaceAll('_', '');
}

export function cleanCEP(code: string): string {
  return code.replaceAll('.', '').replaceAll('-', '').replaceAll('_', '');
}

export function urlWithParams(url: string, params: RequestParameters): string {
  if (!url.includes('?')) url += '?';
  return url + new URLSearchParams(params as any).toString();
}

export async function paginatedAxiosResponse<T>(response: Promise<AxiosResponse>): Promise<PaginatedResponse<T>> {
  return response.then(({ data }) => snakeCaseDictToCamelCaseDict(data) as PaginatedResponse<T>);
}

export function canSendDataToThirdParty<T>(
  data: string,
  desiredSize: number,
  callback: (cleanedData: string) => T
): T | undefined {
  const cleanedData = data.replaceAll('-', '').replaceAll('_', '').replaceAll('/', '');
  if (cleanedData.length !== desiredSize) return;

  return callback(cleanedData);
}

export async function getPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<PaginatedResponse<T>> {
  return paginatedAxiosResponse(using.get(urlWithParams(url, params)));
}

export async function getForcedPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<PaginatedResponse<T>> {
  return using.get(urlWithParams(url, params)).then(({ data }) => ({
    count: data.length,
    results: snakeCaseDictToCamelCaseDict(data) as T[],
  }));
}

export async function getNonPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<T[] | T> {
  return using.get(urlWithParams(url, params)).then(({ data }) => snakeCaseDictToCamelCaseDict(data) as T[]);
}

export async function getNonPaginatedEndpointProd<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<T[] | T> {
  return using.get(urlWithParams(url, params)).then(({ data }) => snakeCaseDictToCamelCaseDict(data) as T[]);
}

export const APP_EVENTS = {
  OPEN_LINK: 'href',
  START: 'start',
  SET_USER_ID: 'userid',
  TOGGLE_SCREENSHOT: 'screenshot',
  REFRESH: 'refresh',
};

export function spawnAppEvent(event: any, data: any = '') {
  if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(`${event}:::${data}`);
}
